import axios from "axios";
import { API_URL } from "../../constants";
import type { CustomField } from "types/CustomField";
import {
  FIXED_LEAVE_TYPE_CODES,
  normalizeLeaveTypeCode,
} from "./leaveCalendar.constants";

function normalizeCode(raw: string) {
  return String(raw || "")
    .trim()
    .toUpperCase();
}

function matchesFileTypeToCode(
  fileType: { template?: string; label?: string },
  code: string
) {
  const c = normalizeCode(code);
  const t = String(fileType.template || "")
    .trim()
    .toUpperCase();
  const l = String(fileType.label || "")
    .trim()
    .toUpperCase();
  return t === c || l === c;
}

function templateTypeId(template: {
  templateType?: string | { id?: string };
}): string | undefined {
  const tt = template.templateType;
  if (typeof tt === "object" && tt?.id) return tt.id;
  if (typeof tt === "string") return tt;
  return undefined;
}

export interface LeaveTemplateInfo {
  id: string;
  name: string;
  subject?: string;
  templateType?: string | { id?: string };
  /** The fields the person fills in for this template; the templates list carries them. */
  customFields?: CustomField[];
}

/**
 * Same rules as backend LeaveTemplateResolver: matches by subject code OR legacy fileType.
 */
export async function resolveLeaveTemplatesForType(
  companyId: string,
  leaveTypeCode: string
): Promise<LeaveTemplateInfo[]> {
  const code = normalizeCode(leaveTypeCode);
  // Optional: keep restricted list if needed, or allow all.

  const [typesRes, templatesRes] = await Promise.all([
    axios.get(`${API_URL}/${companyId}/files/types`),
    axios.get(`${API_URL}/${companyId}/files/templates/fetch-file-templates`),
  ]);

  const fileTypes = (typesRes.data || []) as {
    id: string;
    template?: string;
    label?: string;
  }[];
  const templates = (templatesRes.data || []) as LeaveTemplateInfo[];

  // 1. Direct subject matches (new way) - e.g. subject === "vacation"
  // Note: user entered lowercase values like "sick leave" in the form.
  const subjectMatches = templates.filter(
    (t) => normalizeCode(t.subject || "") === code
  );

  // 2. Legacy matches: match FilesTypes label + subject='user'
  const fileType = fileTypes.find((ft) => matchesFileTypeToCode(ft, code));
  const legacyMatches = fileType
    ? templates.filter(
        (t) => t.subject === "user" && templateTypeId(t) === fileType.id
      )
    : [];

  const combined = [...subjectMatches, ...legacyMatches];
  // Deduplicate by ID
  const unique = Array.from(new Map(combined.map((m) => [m.id, m])).values());
  return unique;
}

export function isLeaveSubject(subject: string): boolean {
  const s = normalizeCode(subject).toLowerCase();
  return (
    s === "user" ||
    FIXED_LEAVE_TYPE_CODES.map((c) => normalizeLeaveTypeCode(c)).includes(s)
  );
}

/**
 * Same rules as backend LeaveTemplateResolver.resolveCanonicalLeaveTypeFromTemplate:
 * explicit leave subject on the template, or legacy subject "user" + FilesTypes label/template.
 */
export function resolveCanonicalLeaveTypeFromTemplate(
  template:
    | {
        subject?: string;
        templateType?:
          | string
          | { id?: string; label?: string; template?: string };
      }
    | null
    | undefined
): string | null {
  if (!template?.subject) return null;
  const sub = normalizeLeaveTypeCode(template.subject);
  for (const code of FIXED_LEAVE_TYPE_CODES) {
    if (normalizeLeaveTypeCode(code) === sub) return code;
  }
  if (sub !== "user") return null;
  const ft = template.templateType;
  if (!ft || typeof ft === "string") return null;
  for (const code of FIXED_LEAVE_TYPE_CODES) {
    if (matchesFileTypeToCode(ft, code)) return code;
  }
  return null;
}
